import type { MonkeysLocale } from "./types";

export const enUS = {
  locale: "en-US",
  messages: {
    pagination: {
      navigation: "Pagination",
      first: "First",
      previous: "Previous",
      next: "Next",
      last: "Last",
      page: ({ page, pageCount }) => `Page ${page} of ${pageCount}`,
      currentPage: ({ page, pageCount }) =>
        `Page ${page} of ${pageCount}, current page`,
      emptyResults: "0 results",
      range: ({ start, end, total }) => `${start}-${end} of ${total}`,
      selected: ({ count }) => `${count} selected`,
      pageSize: "Items per page",
      perLoad: "Items per load",
      jumpToPage: "Jump to page",
      pages: "pages",
      clickToJump: "Click to jump to a page",
    },
    dialog: {
      close: "Close dialog",
    },
    dropdownMenu: {
      actions: "Actions",
    },
    loading: {
      spinner: "Loading",
    },
    layout: {
      resizePanels: "Resize panels",
    },
    steps: {
      label: "Progress",
      current: "Current",
      completed: "Completed",
      error: "Error",
      disabled: "Disabled",
    },
    numberInput: {
      increment: "Increase value",
      decrement: "Decrease value",
    },
    passwordInput: {
      showPassword: "Show password",
      hidePassword: "Hide password",
    },
    multiSelect: {
      placeholder: "Select options",
      empty: "No options",
      removeOption: ({ value }) => `Remove ${value}`,
    },
    table: {
      dragColumn: ({ columnId }) => `Drag column ${columnId}`,
      resizeColumn: ({ columnId }) => `Resize column ${columnId}`,
      sortColumn: ({ columnId }) =>
        columnId ? `Sort column ${columnId}` : "Sort column",
      loadingRow: "Loading table row",
    },
    toolbar: {
      theme: {
        label: "Appearance",
        toggle: "Toggle theme",
        auto: "Auto",
        light: "Light",
        dark: "Dark",
      },
      language: {
        label: "Language",
        switch: "Switch language",
      },
    },
    toast: {
      errorTitle: "Error",
      warningTitle: "Warning",
      defaultErrorDescription: "Something went wrong.",
      defaultWarningDescription: "Please check and try again.",
    },
    dataExplorer: {
      viewTree: {
        actions: "More actions",
        expand: "Expand",
        collapse: "Collapse",
        loading: "Loading...",
        empty: "No views",
        error: "Unable to load views",
      },
    },
    workbench: {
      collection: {
        label: "Collection",
        loading: "Loading items",
        empty: "No items",
        loadError: "Unable to load items",
        refreshError: "Unable to refresh items",
        retry: "Retry",
        loadingMore: "Loading more items",
        permission: "You do not have permission to view this content",
        offline: "You appear to be offline",
        requestAccess: "Request access",
      },
      interactiveTable: {
        columnActions: "Column actions",
        freezeColumn: "Freeze to left",
        freezeEndColumn: "Freeze to right",
        unfreezeColumn: "Unfreeze from left",
        unfreezeEndColumn: "Unfreeze from right",
        unfreezeAllColumns: "Unfreeze all columns",
        resetColumnWidth: "Reset column width",
        frozenColumnTitle: "Frozen column",
      },
    },
    agentWorkbench: {
      label: "Session activity",
      reasoning: "Reasoning",
      plan: "Plan",
      taskNoDetail: "No additional details",
      modes: { chat: "Chat", work: "Work" },
      composer: {
        send: "Send message",
        stop: "Stop generating",
        queue: "Queue message",
        attach: "Add photos or files",
        voice: "Start voice input",
        webSearch: "Web search",
        chatMode: "Chat mode",
        model: "Model",
        reasoning: "Reasoning effort",
        permissions: "Permissions",
        removeAttachment: "Remove attachment",
        editQueued: "Edit queued message",
        deleteQueued: "Delete queued message",
        moveUp: "Move message up",
        moveDown: "Move message down",
        queued: "Queued messages",
        uploading: "Uploading",
        uploadFailed: "Upload failed",
        retryAttachment: "Retry attachment upload",
        noModels: "No models available",
        message: "Message",
        attachmentOnly: "Attachment",
        reasoningOptions: {
          low: "Low",
          medium: "Medium",
          high: "High",
          xhigh: "Extra high",
        },
        permissionOptions: {
          "approval-required": "Approval required",
          "auto-approve": "Auto approve",
          "full-access": "Full access",
        },
      },
      quickStart: {
        label: "Quick start",
        back: "Back",
        addFile: "Add file",
      },
      tool: {
        input: "Input",
        output: "Output",
        status: {
          pending: "Pending",
          running: "Running",
          completed: "Completed",
          failed: "Error",
          denied: "Denied",
        },
      },
      approval: {
        approve: "Approve",
        reject: "Reject",
        approved: "Approved",
        rejected: "Rejected",
      },
      artifact: {
        pending: "Preparing",
        ready: "Ready",
        failed: "Failed",
        open: ({ name }) => `Open ${name}`,
      },
      run: {
        stopping: "Stopping...",
        stoppedAfter: ({ duration }) => `You stopped after ${duration}`,
      },
      process: {
        running: "Working",
        queued: "Queued",
        waitingApproval: "Waiting for approval",
        failed: "Failed",
        stopped: "Stopped",
        processed: "Processed",
        processedFor: ({ duration }) => `Worked for ${duration}`,
        completedCount: ({ completed, total }) =>
          `${completed}/${total} completed`,
      },
      artifactWorkspace: {
        outputs: "Outputs",
        sources: "Sources",
        viewAll: "View all",
        toggleSummary: "Toggle files",
        tabs: "Open files",
        closeTab: ({ name }) => `Close ${name}`,
        closeWorkspace: "Close workspace",
        download: ({ name }) => `Download ${name}`,
        openExternal: ({ name }) => `Open ${name} in a new window`,
        loading: "Loading preview...",
        failed: "Preview unavailable",
        retry: "Try again",
      },
      usage: {
        label: "Context usage",
        context: ({ used, total }) => `${used} / ${total} tokens`,
      },
      navigation: {
        search: "Search agents and sessions",
        searchSessions: "Search sessions",
        searchShortcut: "Focus search",
        clearSearch: "Clear search",
        tabsLabel: "Agent workbench navigation",
        agents: "Agents",
        sessions: "Sessions",
        newAgent: "New Agent",
        newSession: "New Session",
        capabilities: "Capabilities & Extensions",
        settings: "Agent Settings",
        allAgents: "All Agents",
        recentSessions: "Recent Sessions",
        chat: "Chat",
        work: "Work",
        chats: "Chats",
        builtIn: "Built-in",
        noDescription: "No description",
        loading: "Loading...",
        emptyAgents: "No agents yet",
        emptySessions: "No sessions yet",
        noResults: "No matching results",
        retry: "Retry",
        pin: "Pin",
        unpin: "Unpin",
        rename: "Rename",
        delete: "Delete",
        actions: "Actions",
        save: "Save",
        cancel: "Cancel",
        running: "Running",
        completed: "Completed",
        failed: "Failed",
        contextUsage: ({ percent }) => `${percent}% context used`,
      },
      details: {
        title: "Task details",
        description: "Summary and workspace activity",
        duration: "Duration",
        updated: "Updated",
        model: "Model",
        mode: "Mode",
        permissions: "Permissions",
        tokenUsage: "Token usage",
        summary: "Summary",
        summaryEmpty: "No summary is available yet.",
        summaryFailed: "The summary could not be generated.",
        highlights: "Highlights",
        pendingItems: "Pending items",
        tabs: {
          label: "Task detail views",
          summary: "Summary",
          workspace: "Workspace",
        },
        status: {
          queued: "Queued",
          running: "Running",
          waiting_approval: "Waiting for approval",
          stopping: "Stopping",
          stopped: "Stopped",
          completed: "Completed",
          failed: "Failed",
        },
        state: {
          loading: {
            title: "Loading task details",
            description: "Restoring the latest session activity.",
          },
          empty: {
            title: "No task selected",
            description: "Select a task to inspect its details.",
          },
          error: {
            title: "Unable to load task details",
            description: "Try again after the session is available.",
          },
        },
        actions: {
          close: "Close task details",
          copySummary: "Copy summary",
          retry: "Retry",
          retrySummary: "Retry summary",
          resume: "Resume",
          continueInNewTask: "Continue in new task",
          editAndRerun: "Edit and rerun",
          export: "Export",
        },
        workspace: {
          diffs: "Code changes",
          files: "Files",
          terminals: "Terminals",
          tests: "Tests",
          artifacts: "Artifacts",
          exitCode: ({ code }) => `Exit code ${code}`,
          diffStatus: {
            added: "Added",
            modified: "Modified",
            deleted: "Deleted",
            renamed: "Renamed",
          },
          fileOperation: { upsert: "Updated", delete: "Deleted" },
        },
      },
    },
  },
} satisfies MonkeysLocale;
